import { toast } from 'react-toastify';
import { BadgeData, BadgeDesign } from '../../../../shared/user/badgeDesign';
import { PrinterPreferenceMode } from '../cashiercontext';
import { PrinterManagerStatus } from '../printers/PrinterBase';
import * as PrinterBase from '../printers/PrinterBase';
import { BaseCashierAction } from '../reducer/reducer';
import { ZebraPrinter2 } from './ZebraPrinter';

export interface PrintBadgeData {
  badge: BadgeData;
  design: BadgeDesign;
  printerSerial: string;
  reason?: string;
}

interface LabelPrinterConfig extends PrinterBase.PrinterConfig {
  serial: string;
  width: number;
  speed: number;
  autosense: boolean;
  darkness: number;
}

export interface LabelPrinterInfo extends PrinterBase.PrinterInfoBase {
  serial: string;
  config: LabelPrinterConfig;
}

export interface ZebraSaveStatus {
  primaryPrinter?: string;
  secondaryPrinter?: string;
}

export interface ZebraState {
  manager: ZebraPrinter2;
  printers: LabelPrinterInfo[];
  status: PrinterManagerStatus;

  // The serial numbers of the printers that are currently connected.
  primaryPrinter?: string;
  secondaryPrinter?: string;

  // The code is currently rendering a badge, and should not start a new print job.
  rendering: boolean;

  printQueue: PrintBadgeData[];

  saveState: ZebraSaveStatus;
}

interface BaseZebraAction extends BaseCashierAction {
  source: 'zebra';
}

interface ZebraStartQueue extends BaseZebraAction {
  type: 'ZEBRA_START_QUEUE';
  queue: PrintBadgeData[];
}

interface ZebraSetPrinterState extends BaseZebraAction {
  type: 'ZEBRA_SET_PRINTER_STATE';
  status: PrinterManagerStatus;
}

interface ZebraSetSlot extends BaseZebraAction {
  type: 'ZEBRA_SET_SLOT';
  slot: 'primary' | 'secondary';
  serial?: string;
}

interface ZebraTestPrint extends BaseZebraAction {
  type: 'ZEBRA_TEST_PRINT';
  serial: string;
}

interface ZebraAddPrinter extends BaseZebraAction {
  type: 'ZEBRA_ADD_PRINTER';
}

interface ZebraRefreshAvailablePrinters extends BaseZebraAction {
  type: 'ZEBRA_REFRESH_AVAILABLEPRINTERS';
}

interface ZebraPrint extends BaseZebraAction {
  type: 'ZEBRA_PRINT';
  badge: BadgeData;
  design: BadgeDesign;
  printer: PrinterPreferenceMode;
  reason?: string;
}

interface ZebraPrintNextQueue extends BaseZebraAction {
  type: 'ZEBRA_PRINT_NEXT_QUEUE';
}

interface ZebraPrintFinish extends BaseZebraAction {
  type: 'ZEBRA_PRINT_FINISH';
}

export type ZebraAction =
  | ZebraStartQueue
  | ZebraSetPrinterState
  | ZebraSetSlot
  | ZebraTestPrint
  | ZebraAddPrinter
  | ZebraRefreshAvailablePrinters
  | ZebraPrint
  | ZebraPrintFinish
  | ZebraPrintNextQueue;

export function zebraReducer(state: ZebraState, action: ZebraAction): ZebraState {
  switch (action.type) {
    case 'ZEBRA_START_QUEUE': {
      if (action.queue.length === 0) {
        return state;
      }

      state.manager.printBadge(action.queue[0]!);

      return {
        ...state,
        printQueue: action.queue.slice(1),
      };
    }

    case 'ZEBRA_PRINT_NEXT_QUEUE': {
      if (state.printQueue.length === 0) {
        return state;
      }

      state.manager.printBadge(state.printQueue[0]!);

      // Remove the first item from the queue.
      return {
        ...state,
        printQueue: state.printQueue.slice(1),
      };
    }

    case 'ZEBRA_SET_PRINTER_STATE': {
      return {
        ...state,
        status: action.status,
      };
    }

    case 'ZEBRA_SET_SLOT': {
      const { slot, serial } = action;

      const newState =
        slot === 'primary'
          ? { ...state, primaryPrinter: serial }
          : { ...state, secondaryPrinter: serial };

      if (newState.primaryPrinter === state.primaryPrinter) {
        newState.primaryPrinter = undefined;
      }

      if (newState.secondaryPrinter === state.secondaryPrinter) {
        newState.secondaryPrinter = undefined;
      }

      return { ...newState, saveState: state.manager.getSaveStatus(newState) };
    }

    case 'ZEBRA_TEST_PRINT': {
      state.manager.testPrint(action.serial);
      return state;
    }

    case 'ZEBRA_ADD_PRINTER': {
      state.manager.startManualConnect();
      return state;
    }

    case 'ZEBRA_REFRESH_AVAILABLEPRINTERS': {
      const devices = state.manager.getAvailablePrinters();

      return {
        ...state,
        printers: devices,
      };
    }

    case 'ZEBRA_PRINT': {
      if (!state.primaryPrinter) {
        toast.error('No primary printer is configured');
        return state;
      }

      state.manager.print(state.primaryPrinter, action.badge, action.design, action.reason);
      return {
        ...state,
        rendering: true,
      };
    }

    case 'ZEBRA_PRINT_FINISH': {
      // Reset the rendering state after a print job is finished.
      return {
        ...state,
        rendering: false,
      };
    }

    default: {
      return state;
    }
  }
}
